import WhiteOnWhiteLogo from '@/components/Logo/WhiteOnWhiteLogo';
import EnergyPalAdvantage from '@/components/FeatureSection/EnergyPalAdvantage';
import Image from 'next/legacy/image';
import FeaturedOn from '@/components/FeatureSection/FeaturedOn';
import Link from 'next/link';
import { ReactElement, useEffect, useState } from 'react';
import { H3 } from '@/components/Element/Heading';
import SlimFooter from '@/components/Footer/SlimFooter';
import { StarIcon } from '@heroicons/react/solid';
import { classNames } from 'lib/utils';
import ReviewPhoto1 from '@/images/reviews/energypal-5-star-review-mike-l.png';
import ReviewPhoto2 from '@/images/reviews/energypal-5-star-review-christopher-grijalva.png';
import ReviewPhoto3 from '@/images/reviews/energypal-5-star-review-henna-m.png';
import RollingLeadForm from 'components/LeadForm/RollingLeadFormWithReviews';
import FullWidthFormSection from '@/components/LeadForm/FullWidthFormSection';
import { GetServerSideProps } from 'next';
import EHouse from '@/images/energypal-icons/e-house-4.png';
import BestExperience from '@/images/energypal-icons/solar-project-experts-4.png';
import BestPrices from '@/images/energypal-icons/best-products-prices-2.png';
import SolarHouse from 'images/hero/House.jpeg';
import FindEnergyLogo from 'images/partners/Find Energy logo.svg';
import { StaticImageData } from 'next/image';

const stateAbbreviations: { [key: string]: string } = {
  AZ: 'Arizona',
  AL: 'Alabama',
  AK: 'Alaska',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  NS: 'Nova Scotia',
  NB: 'New Brunswick',
  QC: 'Quebec',
  NL: 'Newfoundland and Labrador	',
  MB: 'Manitoba',
  SK: 'Saskatchewan',
  YT: 'Yukon',
  BC: 'British Columbia',
};

const reviews = [
  {
    name: 'Mike L.',
    rating: 5,
    photo: ReviewPhoto1,
    comment: `They walked me through the steps to build out my solar configuration, and define my requirements while trying to get some "nice-to-haves" as well. They then went on to compare companies in my area's warranties, the panels, and a whole lot of other information without pressuring me to choose a specific company. I was completely in control of my choices. After a site survey, it turned out that my initial solar configuration wouldn't work due to roof layout, but they were able to run different configurations of panels to meet my requirements. All in all, I would recommend EnergyPal to friends and family for their service!`,
  },
  {
    name: 'Christopher Grijalva',
    rating: 5,
    photo: ReviewPhoto2,
    comment: `My EnergyPal advisor was very helpful and knowledgeable. he took the time to go over all the aspects of the potential project and explained what would happen and why things were being done. it was helpful to not only see the numbers for the project but he also explained the warranty, financing, tax incentives, and how the project would be installed. Overall I felt he provided good information so that I can make an informed decision.`,
  },
  {
    name: 'Henna M.',
    rating: 5,
    photo: ReviewPhoto3,
    comment: `This was probably the best "Solar" experience I had and I've been shopping around for solar panels for more than two years now. My consultant was awesome! He's honest, friendly, communicative, very easy to talk to, extremely attentive and patient, and willing to answer any questions you may have. I really appreciated his candor about every detail discussed. Felt like I knew exactly what I was signing up for at each step of the way not to mention he ended up giving me the most competitive rate which I could not pass. I'm glad I signed up with them and feel confident that this is indeed the one company with the most competitive package. I have already referred them to a few people and have a feeling there will be more!`,
  },
];

const Features = [
  {
    name: 'Your Trusted Advisor',
    description:
      'We listen, educate, and advise. EnergyPal is an unbiased service to assist you in shopping for the best-fit energy solution for your home.',
    icon: EHouse,
  },
  {
    name: 'Better Products & Prices',
    description:
      'Save more, get more with EnergyPal. Rest easy knowing we secured wholesale prices for you on top-quality equipment & warranties.',
    icon: BestPrices,
  },
  {
    name: 'Stress-Free Experience',
    description: (
      <>
        Our personalized service is the easiest way to switch to solar energy
        with confidence. Don&apos;t believe it? Read our reviews below.
      </>
    ),
    icon: BestExperience,
  },
];

type ReviewBlockProps = {
  review: {
    name: string;
    rating: number;
    photo: StaticImageData;
    comment: string;
  };
};

function ReviewBlock({
  review: { name, comment, photo, rating },
}: ReviewBlockProps) {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg shadow-lg">
      <div className="flex flex-col justify-between flex-1 p-6 bg-white">
        <div className="flex items-center mb-3">
          <div className="flex-shrink-0">
            <span className="sr-only">{name}</span>
            <Image className="w-10 h-10 rounded-full" src={photo} alt={name} />
            {/* <img className="w-10 h-10 rounded-full" src={photo} alt={name} /> */}
          </div>
          <div className="ml-3">
            <p className="pl-1 text-sm font-medium text-gray-900">{name}</p>
            <div className="flex items-center">
              {[0, 1, 2, 3, 4].map((count) => (
                <StarIcon
                  key={count}
                  className={classNames(
                    rating > count ? 'text-yellow-400' : 'text-gray-300',
                    'h-5 w-5 flex-shrink-0'
                  )}
                  aria-hidden="true"
                />
              ))}
            </div>
          </div>
        </div>
        <div className="flex-1">
          <p className="text-base text-left text-gray-500">{comment}</p>
        </div>
      </div>
    </div>
  );
}

export const getServerSideProps: GetServerSideProps<Props> = async (
  context
) => {
  const { cid } = context.query;

  const cidValue = typeof cid === 'string' ? cid : 'bqu20zhlaywcfdyg';
  console.log(cidValue);
  return {
    props: {
      cidValue,
    },
  };
};

type Props = {
  cidValue: string;
};

export default function Page({ cidValue }: Props) {
  const [region, setRegion] = useState('');
  const [city, setCity] = useState(' ');
  useEffect(() => {
    const geoCookie = document.cookie
      .split(';')
      .find((cookie) => cookie.trim().startsWith('geo='))
      ?.split('=')[1];

    if (geoCookie) {
      const { region, city } = JSON.parse(decodeURIComponent(geoCookie));
      const fullStateName = stateAbbreviations[region] || 'United States';
      setCity(city);
      setRegion(fullStateName);
    }
  }, []);
  return (
    <>
      <header className="bg-transparent absolute inset-x-0 top-0 z-10">
        <div className="flex mx-auto justify-between px-4 py-5  max-w-5xl sm:px-6 sm:py-4 lg:px-8 lg:space-x-2">
          <Link href="/" className="flex">
            <span className="sr-only">EnergyPal</span>
            <WhiteOnWhiteLogo className="w-auto h-12" />
          </Link>
          <Image src={FindEnergyLogo} alt="FindEnergy Logo" />
        </div>
      </header>
      <div className="relative flex items-center justify-center pt-24">
        <div className="absolute inset-0">
          <Image
            src={SolarHouse}
            alt="EnergyPal"
            className="object-cover w-full h-full scale-x-100"
            layout="fill"
            priority
          />
        </div>
        <div className="relative text-center px-2">
          <div className="mx-auto max-w-7xl">
            <h1 className="capitalize text-black font-semibold text-4xl md:text-5xl mb-4">
              Get personalized home solar quotes online.
            </h1>
            <h2 className="capitalize font-normal text-black text-xl md:text-2xl mb-8">
              Compare options and find the best deals in {city} {region}
            </h2>

            <div className="mx-auto bg-slate-100 py-12 md:p-12 rounded-xl md:mt-24 mb-24 ">
              <div className="text-black">
                <RollingLeadForm cid={cidValue} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gray-100">
        <EnergyPalAdvantage features={Features} />
      </div>

      <div className="bg-white">
        <div className="max-w-3xl py-12 mx-auto text-center">
          <H3 className="capitalize">What {region} Homeowners are saying</H3>
          <div className="grid max-w-lg grid-cols-1 gap-5 mx-auto mt-6 lg:max-w-none">
            {reviews.map((review) => (
              <ReviewBlock key={review.name} review={review} />
            ))}
          </div>
        </div>
      </div>
      <FullWidthFormSection cid={cidValue} />
      <FeaturedOn />
      <SlimFooter />
    </>
  );
}

Page.getLayout = function getLayout(page: ReactElement) {
  return <>{page}</>;
};
